// src/components/AssignTaskModal.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import dayjs from "dayjs";
import { api } from "../api";

const norm = (s) =>
  String(s || "")
    .trim()
    .toLowerCase();

const isClientLike = (role) => {
  const r = norm(role);
  return r === "client" || r === "customer";
};

const catAlias = (s) => {
  const n = norm(s);
  if (n === "cad") return "canada";
  if (n === "archind" || n === "arch.ind" || n === "arch.ind.")
    return "arch.ind";
  return n;
};

const getProjName = (p) =>
  (p?.projectName || p?.name || "Untitled Project").trim();

const getId = (u) => (typeof u === "string" ? u : u?._id || u?.id || "");

/* ---------------- Compact MultiSelect ---------------- */
function MultiSelectDropdown({
  options = [],
  value = [],
  onChange,
  disabled = false,
  placeholder = "Select...",
  maxPreview = 2,
}) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    const onDocClick = (e) => {
      if (!wrapRef.current) return;
      if (!wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const allSelected = value.length === options.length && options.length > 0;

  const toggle = (opt) => {
    const exists = value.includes(opt);
    const next = exists ? value.filter((v) => v !== opt) : [...value, opt];
    onChange?.(next);
  };
  const selectAll = () => onChange?.(options);
  const clearAll = () => onChange?.([]);

  const preview =
    value.length === 0
      ? placeholder
      : value.length <= maxPreview
      ? value.join(", ")
      : `${value.slice(0, maxPreview).join(", ")} +${
          value.length - maxPreview
        }`;

  return (
    <div ref={wrapRef} style={{ position: "relative", width: "100%" }}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        style={{
          width: "100%",
          boxSizing: "border-box",
          textAlign: "left",
          padding: "10px 12px",
          borderRadius: 6,
          border: "1px solid #ccc",
          background: disabled ? "#f3f4f6" : "#fff",
          color: value.length ? "#111827" : "#6b7280",
          cursor: disabled ? "not-allowed" : "pointer",
        }}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        {preview}
      </button>

      {open && !disabled && (
        <div
          role="listbox"
          style={{
            position: "absolute",
            zIndex: 30,
            top: "calc(100% + 6px)",
            left: 0,
            right: 0,
            background: "#fff",
            border: "1px solid #e5e7eb",
            borderRadius: 8,
            boxShadow: "0 10px 24px rgba(0,0,0,0.12)",
            maxHeight: 260,
            overflowY: "auto",
          }}
        >
          <div
            style={{
              display: "flex",
              gap: 8,
              padding: "8px 10px",
              borderBottom: "1px solid #f3f4f6",
              position: "sticky",
              top: 0,
              background: "#fff",
              zIndex: 1,
            }}
          >
            <button
              type="button"
              onClick={selectAll}
              disabled={allSelected || options.length === 0}
              style={{
                fontSize: 12,
                padding: "6px 10px",
                borderRadius: 6,
                border: "1px solid #e5e7eb",
                background: "#f9fafb",
                cursor:
                  allSelected || options.length === 0
                    ? "not-allowed"
                    : "pointer",
              }}
            >
              Select all
            </button>
            <button
              type="button"
              onClick={clearAll}
              disabled={value.length === 0}
              style={{
                fontSize: 12,
                padding: "6px 10px",
                borderRadius: 6,
                border: "1px solid #e5e7eb",
                background: "#f9fafb",
                cursor: value.length === 0 ? "not-allowed" : "pointer",
              }}
            >
              Clear
            </button>
            <div style={{ marginLeft: "auto", fontSize: 12, color: "#6b7280" }}>
              {value.length} selected
            </div>
          </div>

          {options.length === 0 ? (
            <div style={{ padding: 12, fontSize: 13, color: "#6b7280" }}>
              No options.
            </div>
          ) : (
            options.map((opt) => (
              <label
                key={opt}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  padding: "10px 12px",
                  cursor: "pointer",
                }}
              >
                <input
                  type="checkbox"
                  checked={value.includes(opt)}
                  onChange={() => toggle(opt)}
                />
                <span>{opt}</span>
              </label>
            ))
          )}

          <div
            style={{
              position: "sticky",
              bottom: 0,
              background: "#fff",
              borderTop: "1px solid #f3f4f6",
              padding: 8,
              textAlign: "right",
            }}
          >
            <button
              type="button"
              onClick={() => setOpen(false)}
              style={{
                padding: "8px 12px",
                borderRadius: 8,
                border: "1px solid #d1d5db",
                background: "#fff",
                fontWeight: 600,
              }}
            >
              Done
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ------------------------------ Main modal ------------------------------ */
export default function AssignTaskModal({
  project,
  onClose,
  onAssigned,
  users = [],
  drawingMaster = [],
  includeClients = false,
  fetchUsersIfMissing = true,
}) {
  const [assigneeId, setAssigneeId] = useState("");
  const [typeofProject, setTypeofProject] = useState("");
  const [workType, setWorkType] = useState("");
  const [docs, setDocs] = useState([]);
  const [remark, setRemark] = useState("");
  const [dueDate, setDueDate] = useState("");

  const [localUsers, setLocalUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);

  const [localDM, setLocalDM] = useState([]);
  const [loadingDM, setLoadingDM] = useState(false);

  // Reset on project change
  useEffect(() => {
    setAssigneeId("");
    setTypeofProject("");
    setWorkType("");
    setDocs([]);
    setRemark("");
    setDueDate("");
  }, [project?._id]);

  // ------- Users (fallback fetch) -------
  const effectiveUsers = users?.length ? users : localUsers;

  useEffect(() => {
    if (!fetchUsersIfMissing) return;
    if (users?.length) return;
    let mounted = true;
    (async () => {
      try {
        setLoadingUsers(true);
        const res = await api.get("/users", {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        });
        if (!mounted) return;
        const arr = Array.isArray(res.data) ? res.data : res.data?.users || [];
        setLocalUsers(Array.isArray(arr) ? arr : []);
      } catch {
        if (mounted) setLocalUsers([]);
      } finally {
        if (mounted) setLoadingUsers(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [users, fetchUsersIfMissing]);

  // ------- Drawing Master source -------
  const dmSource = drawingMaster?.length ? drawingMaster : localDM;
  useEffect(() => {
    if (drawingMaster?.length) return;
    let mounted = true;
    (async () => {
      try {
        setLoadingDM(true);
        const res = await api.get("/drawing-master", {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        });
        if (!mounted) return;
        setLocalDM(Array.isArray(res.data) ? res.data : []);
      } catch {
        if (mounted) setLocalDM([]);
      } finally {
        if (mounted) setLoadingDM(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [drawingMaster]);

  // ------- Assignee lists -------
  const projectMemberIds = useMemo(() => {
    const arr = Array.isArray(project?.assignedUsers)
      ? project.assignedUsers
      : [];
    return arr.map((u) => getId(u)).filter(Boolean);
  }, [project]);

  const byId = useMemo(() => {
    return new Map((effectiveUsers || []).map((u) => [getId(u), u]));
  }, [effectiveUsers]);

  const assignedUserObjects = useMemo(() => {
    const raw = Array.isArray(project?.assignedUsers)
      ? project.assignedUsers
      : [];
    return raw
      .map((u) => {
        const id = getId(u);
        const full = byId.get(id);
        return full || (typeof u === "object" ? u : { _id: id });
      })
      .filter(Boolean);
  }, [project, byId]);

  const members = useMemo(() => {
    const list = assignedUserObjects
      .filter((u) => (includeClients ? true : !isClientLike(u.role)))
      .map((u) => ({
        ...u,
        _id: getId(u),
        label: u.name || u.email || getId(u),
      }));
    list.sort((a, b) => (a.label || "").localeCompare(b.label || ""));
    return list;
  }, [assignedUserObjects, includeClients]);

  const others = useMemo(() => {
    const setMembers = new Set(projectMemberIds);
    const nonClient = (effectiveUsers || []).filter((u) =>
      includeClients ? true : !isClientLike(u.role)
    );
    const list = nonClient
      .filter((u) => !setMembers.has(getId(u)))
      .map((u) => ({
        ...u,
        _id: getId(u),
        label: `${u.name || u.email || getId(u)} (not in project)`,
      }));
    list.sort((a, b) => (a.label || "").localeCompare(b.label || ""));
    return list;
  }, [effectiveUsers, projectMemberIds, includeClients]);

  const noUsers = members.length === 0 && others.length === 0;

  // ------- Drawing Master per category -------
  const projCat = catAlias(project?.category);
  const baseDM = Array.isArray(dmSource) ? dmSource : [];

  const dmForProject = useMemo(() => {
    if (!projCat || projCat === "all") return baseDM;
    const byCat = baseDM.filter((d) => catAlias(d.category) === projCat);
    return byCat.length ? byCat : baseDM;
  }, [baseDM, projCat]);

  const typeOptions = useMemo(
    () => [
      ...new Set(dmForProject.map((d) => d.typeofProject).filter(Boolean)),
    ],
    [dmForProject]
  );
  const workTypeOptions = useMemo(
    () => [...new Set(dmForProject.map((d) => d.workType).filter(Boolean))],
    [dmForProject]
  );
  const docOptions = useMemo(
    () => [
      ...new Set(dmForProject.map((d) => d.drawingDocument).filter(Boolean)),
    ],
    [dmForProject]
  );

  const estimated = useMemo(() => {
    const map = new Map();
    dmForProject.forEach((d) =>
      map.set(d.drawingDocument, parseFloat(d.estimatedHours) || 0)
    );
    const sum = (docs || []).reduce((s, name) => s + (map.get(name) || 0), 0);
    return Number(sum.toFixed(2));
  }, [dmForProject, docs]);

  const inputStyle = {
    width: "100%",
    boxSizing: "border-box",
    padding: 10,
    borderRadius: 6,
    border: "1px solid #ccc",
    background: "#fff",
  };

  const labelStyle = { fontSize: 13, fontWeight: 600 };

  const handleAssign = async () => {
    if (!assigneeId) return alert("Please select a user to assign.");
    if (!typeofProject) return alert("Please select Type of Project.");
    const selectedDocs = (docs || []).filter((d) => docOptions.includes(d));
    if (!selectedDocs.length)
      return alert("Please select at least one Drawing & Document.");

    const payload = {
      projectId: project?._id,
      assignedTo: assigneeId,
      typeofProject,
      ...(workType ? { workType } : {}),
      drawingDocuments: selectedDocs,
      estimatedHours: estimated,
      remark,
      dueDate: dueDate ? dayjs(dueDate, "YYYY-MM-DD").toDate() : undefined,
    };

    // Find assignee name for display
    const allUserChoices = [...members, ...others];
    const assigneeObj =
      allUserChoices.find((u) => getId(u) === assigneeId) || null;
    const assignedToName =
      assigneeObj?.name || assigneeObj?.label || assigneeId;

    try {
      // 1) Create the task
      await api.post("/tasks", payload, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "application/json",
        },
      });

      // 2) Post a chat message of type 'task'
      const msgPayload = {
        type: "task",
        content: `📝 Task assigned to ${assignedToName}`,
        task: {
          assignedToId: assigneeId,
          assignedToName,
          typeofProject,
          workType: workType || undefined,
          drawingDocuments: selectedDocs,
          estimatedHours: estimated,
          dueDate: payload.dueDate || undefined,
          remark: remark || undefined,
        },
      };

      await api.post(`/project-messages/${project?._id}/messages`, msgPayload, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "application/json",
        },
      });

      // 3) Fire a local event so chat updates instantly (before socket echo)
      window.dispatchEvent(
        new CustomEvent("project:message:local", {
          detail: {
            projectId: project?._id,
            message: {
              ...msgPayload,
              createdAt: new Date().toISOString(),
            },
          },
        })
      );

      alert("✅ Task assigned");
      window.dispatchEvent(
        new CustomEvent("tasks:changed", {
          detail: { projectId: project?._id },
        })
      );
      onAssigned?.();
      onClose?.();
    } catch (err) {
      const status = err?.response?.status;
      const url = err?.config?.url;
      const code = err?.code;
      const body =
        typeof err?.response?.data === "string"
          ? err.response.data
          : err?.response?.data?.message ||
            JSON.stringify(err?.response?.data || {});
      alert(
        `❌ Failed to assign task.\n` +
          [
            status && `HTTP ${status}`,
            url && `POST ${url}`,
            code && `code=${code}`,
            body && `server says: ${body}`,
          ]
            .filter(Boolean)
            .join(" | ")
      );
    }
  };

  const dmBlocking = loadingDM && !baseDM.length;

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.25)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
        padding: 12,
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%",
          maxWidth: 640,
          background: "#fff",
          borderRadius: 12,
          boxShadow: "0 8px 30px rgba(0,0,0,0.2)",
          padding: 16,
          overflow: "visible",
          boxSizing: "border-box",
        }}
      >
        <h3 style={{ marginTop: 0, marginBottom: 12 }}>
          Assign Task: {getProjName(project)}
          {project?.category ? ` (${project.category})` : ""}
        </h3>

        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <div>
            <div style={labelStyle}>Assign to</div>
            <select
              value={assigneeId}
              onChange={(e) => setAssigneeId(e.target.value)}
              style={inputStyle}
              disabled={loadingUsers && !effectiveUsers.length}
            >
              <option value="">
                {loadingUsers && !effectiveUsers.length
                  ? "Loading users..."
                  : noUsers
                  ? "No users available"
                  : "Select..."}
              </option>

              {members.length > 0 && (
                <optgroup label="Project members">
                  {members.map((u) => (
                    <option key={u._id} value={u._id}>
                      {u.label}
                    </option>
                  ))}
                </optgroup>
              )}

              {others.length > 0 && (
                <optgroup
                  label={members.length > 0 ? "Other users" : "All users"}
                >
                  {others.map((u) => (
                    <option key={u._id} value={u._id}>
                      {u.label}
                    </option>
                  ))}
                </optgroup>
              )}
            </select>

            {noUsers && !(loadingUsers && !effectiveUsers.length) && (
              <div style={{ marginTop: 6, color: "#6b7280", fontSize: 12 }}>
                Tip: Pass the <code>users</code> prop or keep{" "}
                <code>fetchUsersIfMissing</code> enabled.
                {includeClients
                  ? ""
                  : " (Clients are hidden. Set includeClients={true} to show them.)"}
              </div>
            )}
          </div>

          <div>
            <div style={labelStyle}>Type of Project</div>
            <select
              value={typeofProject}
              onChange={(e) => setTypeofProject(e.target.value)}
              disabled={dmBlocking}
              style={inputStyle}
            >
              <option value="">
                {dmBlocking ? "Loading..." : "Select..."}
              </option>
              {typeOptions.map((v) => (
                <option key={v} value={v}>
                  {v}
                </option>
              ))}
            </select>
          </div>

          <div>
            <div style={labelStyle}>Work Type (optional)</div>
            <select
              value={workType}
              onChange={(e) => setWorkType(e.target.value)}
              disabled={dmBlocking}
              style={inputStyle}
            >
              <option value="">
                {dmBlocking ? "Loading..." : "Select..."}
              </option>
              {workTypeOptions.map((v) => (
                <option key={v} value={v}>
                  {v}
                </option>
              ))}
            </select>
          </div>

          <div>
            <div style={labelStyle}>Drawing &amp; Document</div>
            <MultiSelectDropdown
              options={docOptions}
              value={docs}
              onChange={setDocs}
              disabled={dmBlocking}
              placeholder={dmBlocking ? "Loading..." : "Select..."}
            />
          </div>

          <div>
            <div style={labelStyle}>Estimated (auto-sum)</div>
            <input value={estimated} readOnly style={inputStyle} />
          </div>

          <div>
            <div style={labelStyle}>Remark</div>
            <input
              value={remark}
              onChange={(e) => setRemark(e.target.value)}
              placeholder="Optional note..."
              style={inputStyle}
            />
          </div>

          <div>
            <div style={labelStyle}>Due Date (optional)</div>
            <input
              type="date"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
              style={inputStyle}
            />
          </div>

          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
            <button
              onClick={onClose}
              style={{
                padding: "10px 16px",
                borderRadius: 6,
                border: "1px solid #d1d5db",
                background: "#fff",
              }}
            >
              Cancel
            </button>
            <button
              onClick={handleAssign}
              disabled={
                dmBlocking ||
                noUsers ||
                (loadingUsers && !effectiveUsers.length)
              }
              style={{
                padding: "10px 16px",
                borderRadius: 6,
                background: "#343A76",
                color: "#fff",
                border: "none",
                fontWeight: 700,
                opacity:
                  dmBlocking ||
                  noUsers ||
                  (loadingUsers && !effectiveUsers.length)
                    ? 0.6
                    : 1,
                cursor:
                  dmBlocking ||
                  noUsers ||
                  (loadingUsers && !effectiveUsers.length)
                    ? "not-allowed"
                    : "pointer",
              }}
            >
              Assign
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
